/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { AdminKeys } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import { SearchManagementClient } from "../searchManagementClient.js";
import {
  AdminKeysGetOptionalParams,
  AdminKeysGetResponse,
  AdminKeyKind,
  AdminKeysRegenerateOptionalParams,
  AdminKeysRegenerateResponse,
} from "../models/index.js";

/** Class containing AdminKeys operations. */
export class AdminKeysImpl implements AdminKeys {
  private readonly client: SearchManagementClient;

  /**
   * Initialize a new instance of the class AdminKeys class.
   * @param client Reference to the service client
   */
  constructor(client: SearchManagementClient) {
    this.client = client;
  }

  /**
   * Gets the primary and secondary admin API keys for the specified Azure AI Search service.
   * @param resourceGroupName The name of the resource group within the current subscription. You can
   *                          obtain this value from the Azure Resource Manager API or the portal.
   * @param searchServiceName The name of the Azure AI Search service associated with the specified
   *                          resource group.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    searchServiceName: string,
    options?: AdminKeysGetOptionalParams,
  ): Promise<AdminKeysGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, searchServiceName, options },
      getOperationSpec,
    );
  }

  /**
   * Regenerates either the primary or secondary admin API key. You can only regenerate one key at a
   * time.
   * @param resourceGroupName The name of the resource group within the current subscription. You can
   *                          obtain this value from the Azure Resource Manager API or the portal.
   * @param searchServiceName The name of the Azure AI Search service associated with the specified
   *                          resource group.
   * @param keyKind Specifies which key to regenerate. Valid values include 'primary' and 'secondary'.
   * @param options The options parameters.
   */
  regenerate(
    resourceGroupName: string,
    searchServiceName: string,
    keyKind: AdminKeyKind,
    options?: AdminKeysRegenerateOptionalParams,
  ): Promise<AdminKeysRegenerateResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, searchServiceName, keyKind, options },
      regenerateOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Search/searchServices/{searchServiceName}/listAdminKeys",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.AdminKeyResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.searchServiceName,
    Parameters.subscriptionId,
  ],
  headerParameters: [Parameters.accept, Parameters.clientRequestId],
  serializer,
};
const regenerateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Search/searchServices/{searchServiceName}/regenerateAdminKey/{keyKind}",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.AdminKeyResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.searchServiceName,
    Parameters.subscriptionId,
    Parameters.keyKind,
  ],
  headerParameters: [Parameters.accept, Parameters.clientRequestId],
  serializer,
};
